import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const steps = [
  {
    n: "01",
    title: "Business Discovery",
    body: "Deep-dive into your P&L, positioning, brand equity and ambition. We define what winning looks like — with numbers.",
  },
  {
    n: "02",
    title: "Venue Observation",
    body: "Two nights on the floor, in the DJ booth, at the bar. We watch how guests actually move — and where the friction lives.",
  },
  {
    n: "03",
    title: "Financial Analysis",
    body: "Revenue mix, labour ratio, pour costs, spend-per-cover, contribution margin. Every rupee is traced.",
  },
  {
    n: "04",
    title: "Operational Audit",
    body: "Shift design, SOPs, inventory, vendors, kitchen throughput, host desk. What's holding the machine back?",
  },
  {
    n: "05",
    title: "Growth Strategy",
    body: "Menu redesign, pricing architecture, guest journey, retention loops, staff incentives — a plan built for margin.",
  },
  {
    n: "06",
    title: "Implementation Roadmap",
    body: "A 90-day, prioritised roadmap with owners, KPIs and quick wins. Not a deck. A working document.",
  },
];

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "end 20%"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative py-20 md:py-40 bg-[#050505] overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 md:mb-24">
          <div className="lg:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
              Ch. 05 &mdash; The Audit Process
            </div>
            <h2 className="font-display text-white text-[34px] sm:text-4xl md:text-6xl lg:text-[80px] leading-[0.98] tracking-[-0.02em]">
              Six chapters,{" "}
              <em className="font-editorial italic text-[#D4AF37] font-light">
                one clear outcome.
              </em>
            </h2>
          </div>
          <div className="lg:col-span-4 text-white/60 text-sm md:text-base leading-relaxed lg:pl-6">
            A methodology refined across two decades and hundreds of nights on
            the floor. Not theory — evidence.
          </div>
        </div>

        <div ref={ref} className="relative grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Vertical timeline */}
          <div className="hidden md:block md:col-span-1 relative">
            <div className="sticky top-24 h-[70vh]">
              <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-white/10" />
              <motion.div
                style={{ height: lineHeight }}
                className="absolute left-1/2 -translate-x-1/2 top-0 w-px bg-[#D4AF37] shadow-[0_0_16px_rgba(212,175,55,0.7)]"
              />
            </div>
          </div>

          <ol className="md:col-span-11 flex flex-col gap-6 md:gap-10">
            {steps.map((s, i) => (
              <motion.li
                key={s.n}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.7, delay: 0.05 }}
                data-testid={`process-step-${s.n}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 items-start p-5 sm:p-6 md:p-8 rounded-2xl border border-white/8 bg-white/[0.015] backdrop-blur-xl"
              >
                <div className="md:col-span-2 flex md:flex-col items-baseline md:items-start gap-4">
                  <span className="font-display text-[#D4AF37] text-2xl sm:text-3xl md:text-4xl">
                    {s.n}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.24em] sm:tracking-[0.28em] text-white/40 md:mt-2">
                    Step {i + 1}
                  </span>
                </div>
                <div className="md:col-span-4">
                  <h3 className="font-display text-white text-xl sm:text-2xl md:text-[30px] leading-tight">
                    {s.title}
                  </h3>
                </div>
                <div className="md:col-span-6 text-white/70 text-sm md:text-base leading-relaxed">
                  {s.body}
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
